import { axiosInstance as api } from "../helpers/request";
import { IVisualMedia, VisualMediaClassificationEnum } from "../types/models";

const base = (streamId: string) => `/streams/${streamId}/visual-media`;

class VisualMediaService {
  getVisualMediaList(
    streamId: string,
    {
      query = "",
      pageNumber,
      folderId,
      classification,
    }: {
      query?: string;
      pageNumber: number;
      folderId?: string;
      classification: VisualMediaClassificationEnum;
    },
  ) {
    let url = `${base(streamId)}?classification=${classification}&search=${query}&page=${pageNumber}&perPage=10000`;
    if (folderId) {
      url += `&folderId=${folderId}`;
    }
    return api.get<{ data: IVisualMedia[] }>(url);
  }

  getVisualMediaById(streamId: string, id: string) {
    return api.get<IVisualMedia>(`${base(streamId)}/${id}`);
  }

  deleteVisualMedia(streamId: string, id: string) {
    return api.delete<IVisualMedia>(`${base(streamId)}/${id}`);
  }

  deleteVisualMediaBatch(streamId: string, ids: string[]) {
    return api.post(`${base(streamId)}/patch-delete`, { ids });
  }

  getDownloadUrl(streamId: string, id: string) {
    return api.get<{ url: string }>(`${base(streamId)}/${id}/download`);
  }

  updateVisualMedia(streamId: string, data: Pick<IVisualMedia, "id"> | Partial<IVisualMedia>) {
    return api.patch<IVisualMedia>(`${base(streamId)}/${data.id}`, data);
  }

  duplicateVisualMedia(streamId: string, id: string) {
    return api.post<IVisualMedia>(`${base(streamId)}/duplicate/${id}`);
  }

  /** Reserves `contentLength` bytes against the stream's cap for one hour. */
  generatePresignedUrl(
    streamId: string,
    { fileName, contentType, contentLength, requestKey }: { fileName: string; contentType: string; contentLength: number; requestKey?: string },
  ) {
    return api.post<{ url: string; Key: string; intentId: string }>(`${base(streamId)}/presigned/generate`, {
      fileName,
      contentType,
      contentLength,
      requestKey,
    });
  }

  savePresigned(
    streamId: string,
    body: { Key: string; classification: VisualMediaClassificationEnum; originalName: string; folderId?: string },
  ) {
    return api.post<IVisualMedia>(`${base(streamId)}/presigned/save`, body);
  }

  getGoogleDriveFile(streamId: string, fileId: string) {
    return api.get(`${base(streamId)}/gdrive/file/${fileId}`);
  }

  importGoogleDrive(streamId: string, body: { id: string; folderId?: string; requestKey?: string }) {
    return api.post<IVisualMedia>(`${base(streamId)}/gdrive/file/import`, body);
  }

  retryEncode(streamId: string, id: string) {
    return api.post(`${base(streamId)}/${id}/encode/retry`);
  }
}

const VisualMediaInstance = new VisualMediaService();

export default VisualMediaInstance;
